import { Injectable } from '@angular/core';

import {
  DocumentTemplate,
  DocumentPolicy,
  Document,
  DocumentReportFilters,
  DocumentReportRow,
  DocumentTemplateLinkedProducts
} from '../../models/document-template';
import { ListResponse } from '../../models/list-response';

import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class DocumentTemplateService {
  /**
   * A service providing access to document templates via the API.
   */

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Get a page of document templates from the API.
   *
   * @param status - Optionally restrict to a status. Omitted/'active' returns
   *     active templates; 'retired' returns retired templates (PRO-863).
   */
  getDocumentTemplates(pageNumber?: number,
                       pageSize?: number,
                       status?: string): Promise<ListResponse<DocumentTemplate>> {
    return this.apiService.getCollection<DocumentTemplate, {status?: string}>(
      'document-templates/',
      pageNumber,
      pageSize,
      status ? {status} : undefined
    );
  }

  /**
   * Active and retired templates as one list (retired ones have is_removed
   * set), for a filter that may still name a since-retired template.
   */
  getAllDocumentTemplates(): Promise<DocumentTemplate[]> {
    return Promise.all([
      this.getDocumentTemplates(1, 3000),
      this.getDocumentTemplates(1, 3000, 'retired'),
    ]).then(([active, retired]) => [...active.results, ...retired.results]);
  }

  /**
   * Retire a document template (PRO-863), hiding it from the templates list,
   * from the templates available when signing documents, and from
   * document-related settings. Existing documents are preserved.
   */
  retireDocumentTemplate(id: number): Promise<DocumentTemplate> {
    return this.apiService.postToAPI<DocumentTemplate>(
      'document-templates/' + id + '/retire/',
      {}
    );
  }

  /**
   * Restore a previously retired document template (PRO-863), making it active
   * and visible again.
   */
  restoreDocumentTemplate(id: number): Promise<DocumentTemplate> {
    return this.apiService.postToAPI<DocumentTemplate>(
      'document-templates/' + id + '/restore/',
      {}
    );
  }

  /**
   * Get the products (event types) that reference this document template, for
   * the "Linked Products" tab (PRO-863).
   */
  getLinkedProducts(id: number): Promise<DocumentTemplateLinkedProducts> {
    return this.apiService.getFromAPI<DocumentTemplateLinkedProducts>(
      'document-templates/' + id + '/linked-products/'
    );
  }

  /**
   * Get a single document template from the API.
   */
  getDocumentTemplate(id: number): Promise<DocumentTemplate> {
    return this.apiService.getItem<DocumentTemplate>(
      'document-templates/',
      id
    );
  }

  /**
   * Post a new document template to the API.
   *
   * @param documentTemplate - The document template to create.
   * @return The new document template returned from the API.
   */
  addDocumentTemplate(documentTemplate: DocumentTemplate): Promise<DocumentTemplate> {
    // Remove "editing" state from all elements upon saving.
    for (const section of documentTemplate.sections) {
      for (const element of section.elements) {
        element.editing = undefined;
      }
    }

    return this.apiService.postToAPI<DocumentTemplate>(
      'document-templates/',
      {
        document_type: documentTemplate.document_type,
        name: documentTemplate.name,
        is_location_restricted: documentTemplate.is_location_restricted,
        allowed_locations: documentTemplate.allowed_locations,
        can_be_used_for_barcode_check_in: documentTemplate.can_be_used_for_barcode_check_in,
        is_available_in_kiosk_mode: documentTemplate.is_available_in_kiosk_mode,
        is_available_in_climber_app: documentTemplate.is_available_in_climber_app,
        quiz_error_message_kiosk: documentTemplate.quiz_error_message_kiosk,
        quiz_error_message_climber_app: documentTemplate.quiz_error_message_climber_app,
        quiz_error_alert_immediately_climber_app: documentTemplate.quiz_error_alert_immediately_climber_app,
        quiz_error_create_inbox_item_climber_app: documentTemplate.quiz_error_create_inbox_item_climber_app,
        is_available_to_adult_participants: documentTemplate.is_available_to_adult_participants,
        is_available_to_adults_with_minors: documentTemplate.is_available_to_adults_with_minors,
        is_available_to_minors: documentTemplate.is_available_to_minors,
        is_available_to_unsupervised_minors: documentTemplate.is_available_to_unsupervised_minors,
        is_available_to_adults_with_supervised_adults: documentTemplate.is_available_to_adults_with_supervised_adults,
        is_available_to_supervisors_alone: documentTemplate.is_available_to_supervisors_alone,
        is_available_to_supervisors_with_supervisees: documentTemplate.is_available_to_supervisors_with_supervisees,
        adult_min_age: documentTemplate.adult_min_age,
        minor_max_age: documentTemplate.minor_max_age,
        max_secondary_customer_count: documentTemplate.max_secondary_customer_count,
        sections: documentTemplate.sections,
        welcome_text: documentTemplate.welcome_text,
        confirmation_text: documentTemplate.confirmation_text,
        days_to_expiration: documentTemplate.days_to_expiration,
        years_to_expiration: documentTemplate.years_to_expiration,
        expire_when_minor_becomes_adult: documentTemplate.expire_when_minor_becomes_adult,
        send_expiring_soon_email: documentTemplate.send_expiring_soon_email,
        expiring_soon_email_lead_days: documentTemplate.expiring_soon_email_lead_days,
        expiring_soon_email_text: documentTemplate.expiring_soon_email_text,
        expiring_soon_email_include_button: documentTemplate.expiring_soon_email_include_button,
        expiring_soon_email_button_text: documentTemplate.expiring_soon_email_button_text,
        send_expired_email: documentTemplate.send_expired_email,
        expired_email_text: documentTemplate.expired_email_text,
        expired_email_include_button: documentTemplate.expired_email_include_button,
        expired_email_button_text: documentTemplate.expired_email_button_text,
        counts_as_waiver_for_supervisees: documentTemplate.counts_as_waiver_for_supervisees,
        requires_staff_approval: documentTemplate.requires_staff_approval,
        order_number: documentTemplate.order_number,
        granted_adult_proficiencies: documentTemplate.granted_adult_proficiencies,
        granted_adult_non_participant_proficiencies: documentTemplate.granted_adult_non_participant_proficiencies,
        granted_minor_proficiencies: documentTemplate.granted_minor_proficiencies,
        granted_unsupervised_minor_proficiencies: documentTemplate.granted_unsupervised_minor_proficiencies,
        granted_supervised_adult_proficiencies: documentTemplate.granted_supervised_adult_proficiencies,
        granted_supervisor_proficiencies: documentTemplate.granted_supervisor_proficiencies,
        granted_supervisee_proficiencies: documentTemplate.granted_supervisee_proficiencies,
        granted_climber_proficiencies: documentTemplate.granted_climber_proficiencies
      }
    );
  }

  /**
   * Update a document template via the API.
   *
   * @param documentTemplate - The document template, with updated values.
   * @return The updated document template returned from the API.
   */
  updateDocumentTemplate(documentTemplate: DocumentTemplate): Promise<DocumentTemplate> {
    // Remove "editing" state from all elements upon saving.
    for (const section of documentTemplate.sections) {
      for (const element of section.elements) {
        element.editing = undefined;
      }
    }

    // This allows a user to leave the input blank or set it to zero to
    // indicate null.
    // @ts-ignore
    if ((documentTemplate.days_to_expiration === '') || (documentTemplate.days_to_expiration === 0)) {
      documentTemplate.days_to_expiration = null;
    }
    // @ts-ignore
    if ((documentTemplate.years_to_expiration === '') || (documentTemplate.years_to_expiration === 0)) {
      documentTemplate.years_to_expiration = null;
    }

    return this.apiService.patchToAPI<DocumentTemplate>(
      documentTemplate.url,
      {
        name: documentTemplate.name,
        is_location_restricted: documentTemplate.is_location_restricted,
        allowed_locations: documentTemplate.allowed_locations,
        can_be_used_for_barcode_check_in: documentTemplate.can_be_used_for_barcode_check_in,
        is_available_in_kiosk_mode: documentTemplate.is_available_in_kiosk_mode,
        is_available_in_climber_app: documentTemplate.is_available_in_climber_app,
        quiz_error_message_kiosk: documentTemplate.quiz_error_message_kiosk,
        quiz_error_message_climber_app: documentTemplate.quiz_error_message_climber_app,
        quiz_error_alert_immediately_climber_app: documentTemplate.quiz_error_alert_immediately_climber_app,
        quiz_error_create_inbox_item_climber_app: documentTemplate.quiz_error_create_inbox_item_climber_app,
        is_available_to_adult_participants: documentTemplate.is_available_to_adult_participants,
        is_available_to_adults_with_minors: documentTemplate.is_available_to_adults_with_minors,
        is_available_to_minors: documentTemplate.is_available_to_minors,
        is_available_to_unsupervised_minors: documentTemplate.is_available_to_unsupervised_minors,
        is_available_to_adults_with_supervised_adults: documentTemplate.is_available_to_adults_with_supervised_adults,
        is_available_to_supervisors_alone: documentTemplate.is_available_to_supervisors_alone,
        is_available_to_supervisors_with_supervisees: documentTemplate.is_available_to_supervisors_with_supervisees,
        adult_min_age: documentTemplate.adult_min_age,
        minor_max_age: documentTemplate.minor_max_age,
        max_secondary_customer_count: documentTemplate.max_secondary_customer_count,
        sections: documentTemplate.sections,
        welcome_text: documentTemplate.welcome_text,
        confirmation_text: documentTemplate.confirmation_text,
        days_to_expiration: documentTemplate.days_to_expiration,
        years_to_expiration: documentTemplate.years_to_expiration,
        expire_when_minor_becomes_adult: documentTemplate.expire_when_minor_becomes_adult,
        send_expiring_soon_email: documentTemplate.send_expiring_soon_email,
        expiring_soon_email_lead_days: documentTemplate.expiring_soon_email_lead_days,
        expiring_soon_email_text: documentTemplate.expiring_soon_email_text,
        expiring_soon_email_include_button: documentTemplate.expiring_soon_email_include_button,
        expiring_soon_email_button_text: documentTemplate.expiring_soon_email_button_text,
        send_expired_email: documentTemplate.send_expired_email,
        expired_email_text: documentTemplate.expired_email_text,
        expired_email_include_button: documentTemplate.expired_email_include_button,
        expired_email_button_text: documentTemplate.expired_email_button_text,
        counts_as_waiver_for_supervisees: documentTemplate.counts_as_waiver_for_supervisees,
        requires_staff_approval: documentTemplate.requires_staff_approval,
        order_number: documentTemplate.order_number,
        granted_adult_proficiencies: documentTemplate.granted_adult_proficiencies,
        granted_adult_non_participant_proficiencies: documentTemplate.granted_adult_non_participant_proficiencies,
        granted_minor_proficiencies: documentTemplate.granted_minor_proficiencies,
        granted_unsupervised_minor_proficiencies: documentTemplate.granted_unsupervised_minor_proficiencies,
        granted_supervised_adult_proficiencies: documentTemplate.granted_supervised_adult_proficiencies,
        granted_supervisor_proficiencies: documentTemplate.granted_supervisor_proficiencies,
        granted_supervisee_proficiencies: documentTemplate.granted_supervisee_proficiencies,
        granted_climber_proficiencies: documentTemplate.granted_climber_proficiencies
      }
    );
  }

  /**
   * Get a preview of the "document expiring soon" reminder email for the
   * given document template, using the supplied (possibly unsaved) settings.
   */
  previewExpiringSoonEmail(documentTemplateId: number,
                           text: string,
                           includeButton: boolean,
                           leadDays: number,
                           buttonText: string): Promise<{
    subject: string;
    text_content: string;
    html_content: string;
  }> {
    return this.apiService.postToAPI(
      'document-templates/' + documentTemplateId.toString() + '/preview-expiring-soon-email/',
      {
        expiring_soon_email_text: text,
        expiring_soon_email_include_button: includeButton,
        expiring_soon_email_lead_days: leadDays,
        expiring_soon_email_button_text: buttonText
      }
    );
  }

  /**
   * Get a preview of the "document has expired" email for the given document
   * template, using the supplied (possibly unsaved) settings.
   */
  previewExpiredEmail(documentTemplateId: number,
                      text: string,
                      includeButton: boolean,
                      buttonText: string): Promise<{
    subject: string;
    text_content: string;
    html_content: string;
  }> {
    return this.apiService.postToAPI(
      'document-templates/' + documentTemplateId.toString() + '/preview-expired-email/',
      {
        expired_email_text: text,
        expired_email_include_button: includeButton,
        expired_email_button_text: buttonText
      }
    );
  }

  /**
   * Get a page of document policies from the API.
   */
  getDocumentPolicies(pageNumber?: number,
                      pageSize?: number): Promise<ListResponse<DocumentPolicy>> {
    return this.apiService.getCollectionNoFilters<DocumentPolicy>(
      'document-policies/',
      pageNumber,
      pageSize
    );
  }

  /**
   * Get a single document policy from the API.
   */
  getDocumentPolicy(id: number): Promise<DocumentPolicy> {
    return this.apiService.getItem<DocumentPolicy>(
      'document-policies/',
      id
    );
  }

  /**
   * Post a new document policy to the API.
   *
   * @param documentPolicy - The document policy to create.
   * @return The new document policy returned from the API.
   */
  addDocumentPolicy(documentPolicy: DocumentPolicy): Promise<DocumentPolicy> {
    return this.apiService.postToAPI<DocumentPolicy>(
      'document-policies/',
      {
        name: documentPolicy.name,
        status: documentPolicy.status,
        content: documentPolicy.content
      }
    );
  }

  /**
   * Update a document policy via the API.
   *
   * @param documentPolicy - The document policy, with updated values.
   * @return The updated document policy returned from the API.
   */
  updateDocumentPolicy(documentPolicy: DocumentPolicy): Promise<DocumentPolicy> {
    return this.apiService.patchToAPI<DocumentPolicy>(
      documentPolicy.url,
      {
        name: documentPolicy.name,
        status: documentPolicy.status,
        content: documentPolicy.content
      }
    );
  }

  /**
   * Obtain an individual document's info via the API.
   */
  getDocument(id: string): Promise<Document> {
    return this.apiService.getItem<Document>(
      'documents/',
      id
    );
  }

  markDocumentAsValid(id: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'documents/' + id + '/mark-as-valid/',
      {}
    ).then();
  }

  markDocumentAsInvalid(id: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'documents/' + id + '/mark-as-invalid/',
      {}
    ).then();
  }

  getDocumentsReport(pageNumber?: number,
                     pageSize?: number,
                     filters?: DocumentReportFilters,
                     isOperational = false): Promise<ListResponse<DocumentReportRow>> {
    return this.apiService.getCollection<DocumentReportRow, DocumentReportFilters>(
      'documents-report/',
      pageNumber,
      pageSize,
      filters,
      isOperational
    );
  }
}
